import { useEffect, useState } from 'react';

/**
 * Цены курса и шпаргалок: Tribute берёт доллары. Рядом — примерная сумма в
 * валюте посетителя, по часовому поясу устройства (он есть всегда и не
 * требует разрешений), а если пояс ни о чём не говорит — по языку браузера.
 * Курс — тот же сервис, что у цен на главной, с кешем на 6 часов. Без курса
 * показываем только доллары.
 */
const ZONES = {
  RUB: ['Europe/Moscow', 'Europe/Kaliningrad', 'Europe/Samara', 'Europe/Volgograd', 'Europe/Saratov', 'Europe/Ulyanovsk',
    'Europe/Astrakhan', 'Europe/Kirov', 'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Novosibirsk', 'Asia/Barnaul', 'Asia/Tomsk',
    'Asia/Novokuznetsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Chita', 'Asia/Yakutsk', 'Asia/Khandyga', 'Asia/Vladivostok',
    'Asia/Ust-Nera', 'Asia/Magadan', 'Asia/Sakhalin', 'Asia/Srednekolymsk', 'Asia/Kamchatka', 'Asia/Anadyr', 'Europe/Simferopol'],
  KZT: ['Asia/Almaty', 'Asia/Qyzylorda', 'Asia/Qostanay', 'Asia/Aqtobe', 'Asia/Aqtau', 'Asia/Atyrau', 'Asia/Oral'],
  BYN: ['Europe/Minsk'],
  KGS: ['Asia/Bishkek'],
  UZS: ['Asia/Tashkent', 'Asia/Samarkand'],
  UAH: ['Europe/Kyiv', 'Europe/Kiev', 'Europe/Uzhgorod', 'Europe/Zaporozhye'],
  GEL: ['Asia/Tbilisi'],
  AMD: ['Asia/Yerevan'],
  AZN: ['Asia/Baku'],
  TJS: ['Asia/Dushanbe'],
  MDL: ['Europe/Chisinau'],
  GBP: ['Europe/London'],
  CHF: ['Europe/Zurich'],
  PLN: ['Europe/Warsaw'],
  CZK: ['Europe/Prague'],
  TRY: ['Europe/Istanbul'],
  ILS: ['Asia/Jerusalem'],
  AED: ['Asia/Dubai'],
};
// Остальная Европа — евро.
const EUR_ZONES = /^Europe\/(Berlin|Paris|Madrid|Rome|Vienna|Amsterdam|Brussels|Lisbon|Dublin|Helsinki|Tallinn|Riga|Vilnius|Athens|Bratislava|Ljubljana|Luxembourg|Malta|Monaco|Zagreb|Nicosia)$|^Asia\/Famagusta$|^Atlantic\/(Canary|Madeira)$/u;

// Знак, место знака и шаг округления: «около» — значит круглое число.
const STYLE = {
  RUB: { sign: '₽', after: true, step: 10 },
  KZT: { sign: '₸', after: true, step: 100 },
  BYN: { sign: 'Br', after: true, step: 1 },
  KGS: { sign: 'сом', after: true, step: 10 },
  UZS: { sign: 'сум', after: true, step: 1000 },
  UAH: { sign: '₴', after: true, step: 10 },
  GEL: { sign: '₾', after: true, step: 1 },
  AMD: { sign: '֏', after: true, step: 100 },
  AZN: { sign: '₼', after: true, step: 1 },
  TJS: { sign: 'сомони', after: true, step: 1 },
  MDL: { sign: 'лей', after: true, step: 5 },
  EUR: { sign: '€', after: false, step: 0.1 },
  GBP: { sign: '£', after: false, step: 0.1 },
  CHF: { sign: 'CHF', after: false, step: 0.1 },
  PLN: { sign: 'zł', after: true, step: 1 },
  CZK: { sign: 'Kč', after: true, step: 10 },
  TRY: { sign: '₺', after: true, step: 10 },
  ILS: { sign: '₪', after: true, step: 1 },
  AED: { sign: 'AED', after: false, step: 1 },
};

export function localCurrency() {
  let zone = '';
  try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* старый браузер */ }
  for (const [code, zones] of Object.entries(ZONES)) if (zones.includes(zone)) return code;
  if (EUR_ZONES.test(zone)) return 'EUR';
  // Пояс не наш (или скрыт) — по языку: русскоязычный посетитель скорее думает в рублях.
  if (/^(ru|be|kk|ky)\b/iu.test(typeof navigator !== 'undefined' ? navigator.language || '' : '')) return 'RUB';
  return null;
}

const CACHE_KEY = 'localPriceRates.v1';
const TTL_MS = 6 * 60 * 60 * 1000;

function cached(code) {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    if (c?.code === code && Date.now() - c.at < TTL_MS && c.rate > 0) return c.rate;
  } catch { /* приватный режим */ }
  return null;
}

export const usd = (amount) => `$${Number(amount).toFixed(2)}`;

export default function usePrice() {
  const [code] = useState(localCurrency);
  const [rate, setRate] = useState(() => (code ? cached(code) : null));

  useEffect(() => {
    if (!code || rate) return undefined;
    let alive = true;
    fetch(`https://api.fxratesapi.com/latest?base=USD&currencies=${code}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const x = d?.rates?.[code];
        if (!alive || !(x > 0)) return;
        setRate(x);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ code, rate: x, at: Date.now() })); } catch { /* ок */ }
      })
      .catch(() => { /* только доллары */ });
    return () => { alive = false; };
  }, [code, rate]);

  // «≈ 490 ₽» для суммы в долларах; пусто, если валюта неизвестна или курса нет.
  const local = (amount) => {
    if (!code || !rate) return '';
    const st = STYLE[code];
    const n = Math.round((amount * rate) / st.step) * st.step;
    const num = n.toLocaleString('ru-RU', { maximumFractionDigits: st.step < 1 ? 2 : 0, minimumFractionDigits: st.step < 1 ? 2 : 0 });
    return `≈ ${st.after ? `${num} ${st.sign}` : `${st.sign}${num}`}`;
  };
  // «$5.90 · ≈ 490 ₽»
  const both = (amount) => [usd(amount), local(amount)].filter(Boolean).join(' · ');
  const note = code && rate ? 'Цена в долларах, в других валютах — примерно.' : '';
  return { usd, local, both, note };
}
